import { useEffect, useState } from 'react';

import { trpc, TrpcOutputs } from '../../config/trpc';

export type ShipmentForm = TrpcOutputs['getShipmentFormsByRequestId'][number];

type Options = {
  enabled?: boolean;
};

/**
 * Fetches the shipment forms (MAWB + HAWBs) for a request/quote and keeps them
 * live via the onShipmentFormUpdated subscription. Mirrors the useCargos pattern.
 */
export const useShipmentForms = (requestId?: number, quoteId?: number, options: Options = {}) => {
  const enabled = options.enabled ?? true;
  const [forms, setForms] = useState<ShipmentForm[]>([]);

  const query = trpc.getShipmentFormsByRequestId.useQuery(
    { requestId, quoteId },
    { enabled: enabled && !!requestId, refetchOnWindowFocus: false },
  );

  useEffect(() => {
    if (query.data) {
      setForms(query.data);
    }
  }, [query.data]);

  const matchesQuote = (incomingQuoteId?: number | null) => quoteId == null || incomingQuoteId === quoteId;

  const upsertForm = (incoming: ShipmentForm) => {
    setForms((prev) => {
      // If this hook is scoped by quote and the updated row no longer belongs to it,
      // remove it from the local list.
      if (!matchesQuote(incoming.quote_id)) {
        return prev.filter((form) => form.id !== incoming.id);
      }

      const next = [...prev];
      const index = next.findIndex((form) => form.id === incoming.id);
      if (index >= 0) {
        next[index] = incoming;
      } else {
        next.push(incoming);
      }

      next.sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));
      return next;
    });
  };

  trpc.onShipmentFormUpdated.useSubscription(
    { requestId, quoteId },
    {
      enabled: enabled && !!requestId,
      onData: (data) => {
        upsertForm(data.shipmentForm);
      },
      onError: (error) => {
        console.log('onShipmentFormUpdated.error: ', error);
      },
    },
  );

  trpc.onShipmentFormCreated.useSubscription(
    { requestId },
    {
      enabled: enabled && !!requestId,
      onData: (shipmentForm) => {
        upsertForm(shipmentForm);
      },
      onError: (error) => {
        console.log('onShipmentFormCreated.error: ', error);
      },
    },
  );

  trpc.onShipmentFormDeleted.useSubscription(
    { requestId },
    {
      enabled: enabled && !!requestId,
      onData: (shipmentForm) => {
        setForms((prev) => prev.filter((form) => form.id !== shipmentForm.id));
      },
      onError: (error) => {
        console.log('onShipmentFormDeleted.error: ', error);
      },
    },
  );

  return {
    ...query,
    data: forms,
  };
};
